/**
 * intervalCatalog — the app's SINGLE source of truth for candle intervals.
 *
 * BYTE-IDENTICAL TWIN. The frontend copy is the source of truth:
 *     frontend/src/utils/intervalCatalog.ts
 *     backend/src/utils/intervalCatalog.ts
 * Guarded by `node scripts/check-interval-parity.mjs`. Fix a divergence with:
 *     cp frontend/src/utils/intervalCatalog.ts backend/src/utils/intervalCatalog.ts
 * Keep this module DEPENDENCY-FREE (no imports at all) so both sides can hold
 * the same bytes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 *
 * Before this module the app carried ~20 independent hardcoded interval lists
 * that did not agree with each other (the chart picker offered 45/90 but the
 * backtest omitted 10; the HTF mapper had its own set; three different Delta
 * "native" lists disagreed, and one of them claimed 12h which the venue
 * rejects). Every one of those is now an import from here.
 *
 * AN INTERVAL IS AN INTEGER NUMBER OF MINUTES, everywhere: the chart picker,
 * `scheduled_scans.intervalMinutes`, `interval_htf_mappings.interval`, the
 * backtest, `historical_candle_series.intervalMinutes`, `chart_lastUsed_v1`.
 * That stays true — this module adds no new type to any of those columns.
 *
 * The ONE value that is not literally its own minute count is CALENDAR_MONTH
 * (43200). A calendar month is 28-31 days, so no fixed minute count can express
 * it; 43200 is a SENTINEL meaning "one calendar month". Anything that buckets
 * candles by second arithmetic MUST call `isCalendarInterval()` first.
 */

// ─── Well-known values ───────────────────────────────────────────────────────

export const HOUR = 60;
export const DAY = 1440;
export const WEEK = 10080;
/** SENTINEL — "one calendar month" (28-31 days), NOT 30 days of minutes. */
export const CALENDAR_MONTH = 43200;

/**
 * The intervals every user gets without configuring anything. Exactly the set
 * the chart picker has always offered, plus 1M.
 */
export const BUILTIN_INTERVALS: readonly number[] = [
  1, 3, 5, 10, 15, 30, 45, 60, 90, 240, 1440, 10080, CALENDAR_MONTH,
];

// ─── Venues ──────────────────────────────────────────────────────────────────

export type Venue = 'DELTA' | 'KITE' | 'BINANCE' | 'YAHOO';

/**
 * What each venue serves NATIVELY, in minutes. Anything else must be resampled
 * from one of these (see `planResample`).
 *
 * DELTA — verified against the live venue 2026-09-04. Its own error payload is
 *   the authority: "Allowed values are 5s,1m,3m,5m,15m,30m,1h,2h,4h,6h,1d,1w".
 *   NOTE 12h (720) is NOT served despite older code listing it as native;
 *   requesting it returned HTTP 400 from Delta. 5s is real, but the app's
 *   interval type is integer minutes, so sub-minute is out of scope.
 * KITE  — minute,3minute,5minute,10minute,15minute,30minute,60minute,day.
 * BINANCE — backtest-only source; this is the subset the app requests.
 * YAHOO — GLOBAL symbols + replay. Yahoo's own list is wider (2m/90m/1mo) but
 *   the app has never asked for those.
 */
export const VENUE_NATIVE: Record<Venue, readonly number[]> = {
  DELTA:   [1, 3, 5, 15, 30, 60, 120, 240, 360, 1440, 10080],
  KITE:    [1, 3, 5, 10, 15, 30, 60, 1440],
  BINANCE: [1, 3, 5, 15, 30, 60, 240, 1440, 10080],
  YAHOO:   [1, 5, 15, 30, 60, 1440, 10080],
};

/** Exchange code → the venue that actually serves its candles. */
export function venueForExchange(exchange: string | null | undefined): Venue {
  const e = (exchange || '').toUpperCase();
  if (e === 'GLOBAL') return 'YAHOO';
  if (e === 'DELTA' || e === 'SYNTH') return 'DELTA';
  return 'KITE';
}

/**
 * Seconds from midnight to the session open, in the IST-SHIFTED frame the whole
 * chart stack uses (every candle timestamp has +19800 added so the axis reads
 * IST while lightweight-charts treats it as UTC).
 *   NSE / BSE / NFO : 09:15 IST → 33300
 *   MCX / CDS / BFO : 09:00 IST → 32400
 *   DELTA / GLOBAL  : 24x7, no session → 0
 */
export function sessionOpenOffsetSec(exchange: string | null | undefined): number {
  const e = (exchange || '').toUpperCase();
  if (e === 'DELTA' || e === 'GLOBAL' || e === 'SYNTH') return 0;
  if (e === 'MCX' || e === 'CDS' || e === 'BFO' || e === 'NCO') return 32400;
  return 33300;
}

// ─── Labels ──────────────────────────────────────────────────────────────────

/**
 * THE interval label for the whole app. Reproduces exactly what the chart
 * picker has always shown for the builtin set (1m 3m 5m 10m 15m 30m 45m 1H 90m
 * 4H 1D 1W) and extends the same rules to any custom value.
 *
 * Case matters and is deliberate: 'm' = minutes, 'M' = month — TradingView's
 * own convention.
 */
export function intervalLabel(minutes: number): string {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return '';
  if (m === CALENDAR_MONTH) return '1M';
  if (m >= WEEK && m % WEEK === 0) return `${m / WEEK}W`;
  if (m >= DAY && m % DAY === 0) return `${m / DAY}D`;
  if (m >= HOUR && m % HOUR === 0) return `${m / HOUR}H`;
  return `${m}m`;
}

/**
 * Inverse of `intervalLabel`. Accepts '1M' | '2W' | '1D' | '4H' | '20m' and a
 * bare number (already minutes).
 *
 * DO NOT confuse this with `frontend/src/utils/timeframe.ts#tfToMinutes`, which
 * parses TRADINGVIEW ALERT payloads and case-INsensitively maps 'M' to MINUTES
 * ('1M' → 1). That mapping is load-bearing for alert→chart navigation and is
 * deliberately left alone. This function is for the app's OWN labels only, and
 * is case-SENSITIVE.
 */
export function parseIntervalLabel(label: string | number | null | undefined): number | null {
  if (label == null) return null;
  if (typeof label === 'number') return Number.isInteger(label) && label > 0 ? label : null;
  const s = String(label).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) { const n = parseInt(s, 10); return n > 0 ? n : null; }
  const m = s.match(/^(\d+)\s*(M|W|D|H|m|h)$/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!(n > 0)) return null;
  switch (m[2]) {
    case 'M': return n === 1 ? CALENDAR_MONTH : null;  // only 1M is defined
    case 'W': return n * WEEK;
    case 'D': return n * DAY;
    case 'H': case 'h': return n * HOUR;
    case 'm': return n;
    default: return null;
  }
}

// ─── Classification ──────────────────────────────────────────────────────────

/** True for intervals needing CALENDAR bucketing, not second arithmetic. */
export function isCalendarInterval(minutes: number): boolean {
  return Number(minutes) === CALENDAR_MONTH;
}

/**
 * Can this interval be CHARTED at all?
 *   - any integer 1..1440 minutes (non-divisors get a ragged last bucket per
 *     day, exactly as TradingView does)
 *   - whole-day multiples above 1440 (2D, 3D, 1W = 7D, 2W …)
 *   - the 1M sentinel
 * Anything else has no defensible bucket anchor — see `describeUnsupported`.
 */
export function isChartable(minutes: number): boolean {
  const m = Number(minutes);
  if (!Number.isInteger(m) || m < 1) return false;
  if (m === CALENDAR_MONTH) return true;
  if (m <= DAY) return true;
  return m % DAY === 0;
}

/**
 * Can this interval drive a SCHEDULED SCAN / AUTOTRADE?
 *
 * Only when the scheduler's fire grid provably coincides with the candle grid.
 * `computeNextRunAt`'s MINUTE branch snaps to an EPOCH-anchored grid of
 * `intervalMinutes`; 86400 seconds is a whole multiple of any divisor of 1440
 * expressed in seconds, and the epoch is a whole number of days, so for those
 * values the epoch grid IS the UTC-day-anchored candle grid. For a non-divisor
 * (7m, 23m) it is not, and the scan would fire mid-bar forever.
 *
 * Daily / weekly / monthly are eligible because they run on a DAY cadence.
 */
export function isScanEligible(minutes: number): boolean {
  const m = Number(minutes);
  if (!isChartable(m)) return false;
  if (m === CALENDAR_MONTH || m === WEEK || m === DAY) return true;
  if (m > DAY) return false;              // 2D / 3D have no cadence type
  return DAY % m === 0;
}

/** Nearest scan-eligible interval walking in `dir` (-1 down, +1 up). */
export function nearestScanEligible(minutes: number, dir: 1 | -1): number | null {
  for (let v = Math.round(Number(minutes)) + dir; v >= 1 && v <= DAY; v += dir) {
    if (isScanEligible(v)) return v;
  }
  return null;
}

/** Why an interval was refused — user-facing, and never a silent drop. */
export function describeUnsupported(minutes: number): string | null {
  const m = Number(minutes);
  if (!Number.isFinite(m)) return 'Interval must be a number of minutes.';
  if (!Number.isInteger(m)) return 'Interval must be a whole number of minutes — fractional minutes are not supported.';
  if (m < 1) return 'Interval must be at least 1 minute. Sub-minute candles are not supported: the interval is stored as whole minutes throughout the app.';
  if (m > DAY && m !== CALENDAR_MONTH && m % DAY !== 0) {
    const near = Math.max(1, Math.round(m / DAY)) * DAY;
    return `${m} minutes is longer than a day but not a whole number of days, so it has no stable bucket anchor. Use a whole number of days (e.g. ${near} = ${intervalLabel(near)}).`;
  }
  return null;
}

/** Why an otherwise-chartable interval cannot be scanned. */
export function describeScanIneligible(minutes: number): string | null {
  const m = Number(minutes);
  if (isScanEligible(m)) return null;
  if (!isChartable(m)) return describeUnsupported(m);
  if (m > DAY) return `${intervalLabel(m)} can be charted but not scanned — the scheduler has no cadence longer than a day other than 1D, 1W and 1M.`;
  const alts = [nearestScanEligible(m, -1), nearestScanEligible(m, 1)]
    .filter((v): v is number => v != null).map(intervalLabel).join(' or ');
  return `${intervalLabel(m)} can be charted but not scanned: ${m} does not divide 1440, so the scan grid would drift off the candle grid every day.${alts ? ` Nearest scannable: ${alts}.` : ''}`;
}

/** Every scan-eligible value, ascending — the divisors of 1440, plus 1W and 1M. */
export function scanEligibleLadder(): number[] {
  const out: number[] = [];
  for (let m = 1; m <= DAY; m++) if (DAY % m === 0) out.push(m);
  out.push(WEEK, CALENDAR_MONTH);
  return out;
}

// ─── Scheduler cadence ───────────────────────────────────────────────────────

export interface Cadence { scheduleType: 'MINUTE' | 'HOUR' | 'DAY'; scheduleValue: number; }

/**
 * The largest scheduleValue each cadence type accepts.
 *
 * MINUTE and HOUR are capped because `computeNextRunAt` snaps them to a grid
 * WITHIN the day — 60+ minutes or 24+ hours has no such grid to land on, and the
 * result would drift. DAY is a plain "every N days from a time of day", so it
 * only needs a sane upper bound.
 *
 * This lives in the shared catalog because the SERVER validates against it and
 * the schedule editor has to clamp to the same numbers. They were previously two
 * hardcoded copies, and a user who set Days/100 and then switched the unit to
 * Minutes sent 100 and got back a 400 to decode from a toast.
 */
export function cadenceValueMax(scheduleType: 'MINUTE' | 'HOUR' | 'DAY'): number {
  if (scheduleType === 'MINUTE') return 59;
  if (scheduleType === 'HOUR') return 23;
  return 365;
}

/**
 * The cadence whose fire grid matches this interval's candle grid. The scan UI
 * auto-fills this; the user may still override it.
 *
 * ≥1D uses DAY/1 rather than DAY/7 or DAY/30: a weekly or monthly bar closes on
 * a calendar boundary that an "every N days from a fixed time" rule cannot track
 * without drifting. Re-evaluating daily catches the close on the day it happens,
 * and the engines' closed-bar guards plus the duplicate-signal partial index
 * stop the intervening runs from double-firing.
 */
export function cadenceFor(minutes: number): Cadence {
  const m = Number(minutes);
  if (m >= DAY || m === CALENDAR_MONTH) return { scheduleType: 'DAY', scheduleValue: 1 };
  if (m % HOUR === 0 && 24 % (m / HOUR) === 0) return { scheduleType: 'HOUR', scheduleValue: m / HOUR };
  return { scheduleType: 'MINUTE', scheduleValue: m };
}

// ─── Resample planning ───────────────────────────────────────────────────────

export interface ResamplePlan {
  /** The native interval to FETCH from the venue. */
  baseMinutes: number;
  /** How many base bars make one target bar. 1 = fetch direct. 0 = variable
   *  (calendar month — the count differs per month). */
  ratio: number;
  /** True when the fetched bars must be bucketed into the target. */
  needsAggregation: boolean;
}

export interface ResampleRefusal { unsupported: true; reason: string; }

export function isRefusal(p: ResamplePlan | ResampleRefusal): p is ResampleRefusal {
  return (p as ResampleRefusal).unsupported === true;
}

/**
 * How to build `targetMinutes` bars on `venue`: fetch the LARGEST native
 * interval that divides the target, then aggregate. Refuses honestly rather
 * than silently substituting a different bar size — which is exactly what the
 * code this replaces did (a 4H scan on Kite received 60m bars; a 4H GLOBAL
 * chart received 60m bars; a 1W scan on Kite received daily bars).
 */
export function planResample(venue: Venue, targetMinutes: number): ResamplePlan | ResampleRefusal {
  const m = Number(targetMinutes);
  const why = describeUnsupported(m);
  if (why) return { unsupported: true, reason: why };

  const natives = VENUE_NATIVE[venue];
  if (!natives || !natives.length) return { unsupported: true, reason: `Unknown venue "${venue}".` };

  // A calendar month is always built from daily bars.
  if (isCalendarInterval(m)) {
    if (!natives.includes(DAY)) return { unsupported: true, reason: `${venue} does not serve daily candles, so 1M cannot be built.` };
    return { baseMinutes: DAY, ratio: 0, needsAggregation: true };
  }

  if (natives.includes(m)) return { baseMinutes: m, ratio: 1, needsAggregation: false };

  let best = 0;
  for (const n of natives) if (n < m && m % n === 0 && n > best) best = n;
  if (!best) {
    return {
      unsupported: true,
      reason: `${intervalLabel(m)} cannot be built on ${venue}: none of the intervals it serves (${natives.map(intervalLabel).join(', ')}) divides ${m} minutes.`,
    };
  }
  return { baseMinutes: best, ratio: m / best, needsAggregation: true };
}

// ─── Higher timeframe ────────────────────────────────────────────────────────

/**
 * The automatic HTF ladder. DELIBERATELY STOPS AT 1W: adding CALENDAR_MONTH
 * here would silently flip every user's un-overridden 1W chart from a 1W HTF to
 * a 1M one, because `fallbackHtf(10080)` would then find 43200 ≥ 3×10080. 1M as
 * an HTF is opt-in through an explicit interval_htf_mappings row only.
 */
export const HTF_LADDER: readonly number[] = [1, 3, 5, 10, 15, 30, 45, 60, 90, 240, 1440, 10080];

/** First ladder rung ≥ 3× base, else a few rungs up; base itself when nothing fits. */
export function fallbackHtf(base: number): number {
  const idx = HTF_LADDER.indexOf(base);
  if (idx >= 0) {
    for (let j = idx + 1; j < HTF_LADDER.length; j++) if (HTF_LADDER[j] >= base * 3) return HTF_LADDER[j];
    return HTF_LADDER[Math.min(HTF_LADDER.length - 1, idx + 3)];
  }
  // Off-ladder (a custom interval): the smallest rung ≥ 3× base, so a 20m chart
  // gets a 60m HTF instead of silently getting no HTF at all.
  for (const rung of HTF_LADDER) if (rung >= base * 3) return rung;
  return base;
}

/** The user's interval_htf_mappings override if present (and > base), else the ladder. */
export function resolveHtfInterval(base: number, map: Record<number, number>): number {
  if (map[base] && map[base] > base) return map[base];
  return fallbackHtf(base);
}

// ─── Bucket boundaries ───────────────────────────────────────────────────────

/** Epoch Monday 00:00 (1970-01-05) — the anchor for day- and week-multiples. */
const EPOCH_MONDAY = 345600;

/**
 * The start of the bucket that `timeSec` belongs to. Handles the calendar month
 * too, so no caller has to special-case it before doing arithmetic on the result.
 */
export function bucketStartFor(timeSec: number, targetMinutes: number, offsetSec = 0): number {
  if (isCalendarInterval(targetMinutes)) {
    const d = new Date(timeSec * 1000);
    return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000);
  }
  const size = targetMinutes * 60;

  if (targetMinutes >= DAY && targetMinutes % DAY === 0) {
    // Day- and week-multiples: fixed buckets anchored to an epoch MONDAY, so a
    // 1W bucket opens Monday and a 1D bucket opens at midnight (EPOCH_MONDAY is
    // itself a whole number of days, so both fall out of the same expression).
    return timeSec - (((timeSec - EPOCH_MONDAY) % size + size) % size);
  }

  // Intraday: the grid restarts at each session open, so a target that does not
  // divide the session leaves a short ragged final bucket — the same thing
  // TradingView does. `dayStart` is the greatest value <= timeSec congruent to
  // offsetSec (mod 86400); there is NO trailing "+ offsetSec" here (that was D1).
  const dayStart = timeSec - (((timeSec - offsetSec) % 86400) + 86400) % 86400;
  return dayStart + Math.floor((timeSec - dayStart) / size) * size;
}

/** Start of the NEXT bucket after the one containing `timeSec`. */
export function nextBucketStart(timeSec: number, targetMinutes: number, offsetSec = 0): number {
  if (isCalendarInterval(targetMinutes)) {
    const d = new Date(timeSec * 1000);
    return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1) / 1000);
  }
  const start = bucketStartFor(timeSec, targetMinutes, offsetSec);
  const next = start + targetMinutes * 60;
  if (targetMinutes >= DAY) return next;
  // Intraday: `bucketStartFor` RESTARTS the grid at every day boundary — the
  // session open on a session venue, midnight on a 24x7 one — so a target that
  // does not divide the day leaves a short ragged final bucket, and that bucket
  // ends when the next day's grid starts.
  //
  // The `|| !offsetSec` that used to short-circuit here was a bug: it exempted
  // exactly the 24x7 venues, whose offset is 0. On Delta a 420m (7H) day is
  // 7H+7H+7H+3H, and this function claimed the 21:00 bar ran to 04:00 while
  // `bucketStartFor` put 02:00 back in the 00:00 bucket — the two disagreed.
  // Nothing depended on the old answer: the only frontend caller
  // (candleTransforms.dropFormingHtfBars) passes a CALENDAR interval, and the
  // only backend callers are verifyIntervals' NSE cases, which have a non-zero
  // offset and so always took the clamp.
  const dayStart = timeSec - (((timeSec - offsetSec) % 86400) + 86400) % 86400;
  return Math.min(next, dayStart + 86400);
}

/** Greatest common divisor of two non-negative integers. gcd(x, 0) === x. */
function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a)), y = Math.abs(Math.round(b));
  while (y) { const t = x % y; x = y; y = t; }
  return x;
}

/**
 * The SCAN TICK cadence for an interval: how often a scheduled scan must look so
 * that it never misses a bar close, expressed in the only cadences the scheduler
 * has (MINUTE / HOUR / DAY).
 *
 * This is deliberately NOT `cadenceFor`. `cadenceFor` answers "which cadence
 * IS this bar size", and it only has an answer when the interval divides the day
 * — it returns {MINUTE, 420} for 7H, which `validateScheduleInputs` then rejects
 * outright because MINUTE is capped at 59. This answers the weaker and always
 * answerable question: "which cadence's fire grid CONTAINS every close of this
 * bar size". A scan may then tick more often than the bar and emit at most once
 * per bar, because every engine evaluates closed bars only and `strategy_signals`
 * dedups per candleTime.
 *
 * The maths. In the chart's IST-shifted frame a bucket opens at
 * `dayStart + k*minutes` where `dayStart ≡ sessionOffsetSec (mod 86400)`, so in
 * real UTC every boundary sits at `(sessionOffsetSec/60 − 330) + k*minutes`
 * minutes past a UTC midnight. The scheduler's grids are epoch-anchored and the
 * epoch is a whole number of days, so a grid of `g` minutes hits all of them iff
 * `g` divides the day, the interval, AND that offset — i.e. their gcd.
 *
 * A NATIVELY-served interval is different: those bars come off the venue's own
 * grid (Delta rolls 4H at 00/04/08… UTC, which is 05:30 IST — NOT the app's
 * 04:00 IST bucket), so only the day and the interval constrain it. That is the
 * default when no venue is supplied, which makes this a safe drop-in wherever
 * `cadenceFor` was used.
 */
export function scanTickCadence(
  minutes: number,
  opts: { venue?: Venue; sessionOffsetSec?: number } = {},
): Cadence {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return { scheduleType: 'DAY', scheduleValue: 1 };
  if (m >= DAY || isCalendarInterval(m)) return { scheduleType: 'DAY', scheduleValue: 1 };

  let g = gcd(DAY, m);
  if (opts.venue) {
    const plan = planResample(opts.venue, m);
    if (!isRefusal(plan) && plan.needsAggregation) {
      // App-bucketed: the IST day start pins the phase of the whole grid.
      const offMin = (((Math.round((opts.sessionOffsetSec || 0) / 60) - 330) % DAY) + DAY) % DAY;
      g = gcd(g, offMin);
    }
  }
  if (g < 1) g = 1;

  if (g % 60 === 0 && 24 % (g / 60) === 0 && g / 60 <= 23) {
    return { scheduleType: 'HOUR', scheduleValue: g / 60 };
  }
  if (g <= 59) return { scheduleType: 'MINUTE', scheduleValue: g };
  // g divides 1440 but is neither <= 59 nor a whole-hour cadence (72, 80, 90,
  // 96, 144, 160, 288). Step down to its largest divisor the MINUTE cadence can
  // express; a divisor of a divisor of 1440 still divides 1440, so the grid
  // stays day-aligned and simply fires more often than strictly required.
  for (let v = 59; v >= 1; v--) if (g % v === 0) return { scheduleType: 'MINUTE', scheduleValue: v };
  return { scheduleType: 'MINUTE', scheduleValue: 1 };
}

/**
 * How a scan on this interval will actually be driven, in the user's words.
 * Replaces the old "you cannot scan this" framing: every chartable interval can
 * now be scanned, it is only the TICK that differs.
 */
export function describeScanCadence(
  minutes: number,
  opts: { venue?: Venue; sessionOffsetSec?: number } = {},
): string {
  const m = Number(minutes);
  const c = scanTickCadence(m, opts);
  const tick = c.scheduleType === 'DAY' ? 'once a day'
    : c.scheduleType === 'HOUR' ? `every ${c.scheduleValue === 1 ? 'hour' : `${c.scheduleValue} hours`}`
    : `every ${c.scheduleValue} min`;
  if (isScanEligible(m)) {
    return `${intervalLabel(m)} bars close on the scan grid, so a scan ticking ${tick} lands on every close.`;
  }
  return `${intervalLabel(m)} does not divide a day (1440 % ${m} = ${DAY % m}), so no cadence equals it. `
    + `A scan ticking ${tick} covers every close instead: it evaluates CLOSED bars only and logs at most `
    + `one signal per bar, so the extra ticks cost a data refresh and nothing else.`;
}
